import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  Download,
  Lock,
  Pencil,
  Plus,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { db, storage } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { getUserData } from '../../services/accessControlService';
import BackButton from '../../components/BackButton';
import PortalBackground from '../../components/PortalBackground';
import BBALoader from '../../components/BBALoader';
import { FIELD_CLASS, LABEL_CLASS } from '../../components/crm/CrmFilterUi';

/**
 * CRM ▸ Templates ▸ Branding — BBA's internal brand kit (logos, colors,
 * typography, voice, brand files). Owner only: the route redirects everyone
 * else, the Templates card is hidden, and firestore/storage rules lock
 * brandKit/** and brand-kit/** to Owners.
 *
 * Everything lives on one doc, brandKit/bba, as ordered arrays.
 */

const KIT_DOC = doc(db, 'brandKit', 'bba');

const TABS = [
  { id: 'logos', label: 'Logos', emoji: '🏷️' },
  { id: 'colors', label: 'Colors', emoji: '🎨' },
  { id: 'typography', label: 'Typography', emoji: '🔤' },
  { id: 'voice', label: 'Voice & Messaging', emoji: '🗣️' },
  { id: 'files', label: 'Files', emoji: '📁' },
];

const LOGO_VARIANTS = ['Primary', 'Secondary', 'Icon / Mark', 'Wordmark', 'Monochrome', 'Favicon', 'Other'];
const LOGO_BACKGROUNDS = [
  { id: 'light', label: 'Light background' },
  { id: 'dark', label: 'Dark background' },
  { id: 'transparent', label: 'Transparent (checker)' },
];
const FONT_ROLES = ['Heading', 'Body', 'Accent', 'Monospace'];
const FILE_CATEGORIES = ['Brand Guidelines', 'Templates', 'Social', 'Print', 'Photography', 'Other'];

const card = 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800';
const btnPrimary =
  'inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-medium shadow-sm transition disabled:opacity-50';
const btnSecondary =
  'inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition';
const iconBtn =
  'p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white transition';

const newId = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

const safeFileName = (name) => String(name || 'file').replace(/[^a-zA-Z0-9._-]+/g, '-');

const normalizeHex = (value) => {
  let v = String(value || '').trim().replace(/^#/, '');
  if (/^[0-9a-f]{3}$/i.test(v)) v = v.split('').map((c) => c + c).join('');
  return /^[0-9a-f]{6}$/i.test(v) ? `#${v.toUpperCase()}` : null;
};

const hexToRgb = (hex) => {
  const h = normalizeHex(hex);
  if (!h) return null;
  const n = parseInt(h.slice(1), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};

const rgbToCmyk = ({ r, g, b }) => {
  const rr = r / 255;
  const gg = g / 255;
  const bb = b / 255;
  const k = 1 - Math.max(rr, gg, bb);
  if (k >= 1) return { c: 0, m: 0, y: 0, k: 100 };
  const pct = (x) => Math.round(((1 - x - k) / (1 - k)) * 100);
  return { c: pct(rr), m: pct(gg), y: pct(bb), k: Math.round(k * 100) };
};

// Dark text on light swatches, white on dark ones (WCAG relative luminance).
const isLightColor = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return true;
  const lin = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const L = 0.2126 * lin(rgb.r) + 0.7152 * lin(rgb.g) + 0.0722 * lin(rgb.b);
  return L > 0.45;
};

const googleFontHref = (family, weights) => {
  const ws = String(weights || '')
    .split(/[\s,]+/)
    .map((w) => parseInt(w, 10))
    .filter((w) => w >= 100 && w <= 900)
    .sort((a, b) => a - b);
  const wght = ws.length ? `:wght@${[...new Set(ws)].join(';')}` : '';
  return `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family.trim()).replace(/%20/g, '+')}${wght}&display=swap`;
};

const linesToList = (text) =>
  String(text || '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

async function downloadRemoteFile(url, filename) {
  try {
    const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
    if (!res.ok) throw new Error(String(res.status));
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename || 'download';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

const fileExt = (name) => {
  const m = String(name || '').match(/\.([a-z0-9]+)$/i);
  return m ? m[1].toLowerCase() : '';
};

const formatBytes = (n) => {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const CHECKER_STYLE = {
  backgroundImage:
    'linear-gradient(45deg,#e5e7eb 25%,transparent 25%),linear-gradient(-45deg,#e5e7eb 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#e5e7eb 75%),linear-gradient(-45deg,transparent 75%,#e5e7eb 75%)',
  backgroundSize: '16px 16px',
  backgroundPosition: '0 0,0 8px,8px -8px,-8px 0',
  backgroundColor: '#ffffff',
};

/* ─────────────────────────── shared bits ─────────────────────────── */

const Modal = ({ title, onClose, children, footer }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onMouseDown={onClose}>
    <div
      className="w-full max-w-lg rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-xl"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-6 pt-5 pb-3">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
        <button type="button" onClick={onClose} className={iconBtn} aria-label="Close">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="px-6 pb-2 space-y-4 max-h-[70vh] overflow-y-auto">{children}</div>
      <div className="flex justify-end gap-2 px-6 py-4">{footer}</div>
    </div>
  </div>
);

const EmptyState = ({ emoji, title, body, action }) => (
  <div className={`${card} rounded-2xl shadow-sm p-10 text-center`}>
    <div className="mx-auto mb-3 w-14 h-14 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-2xl">
      {emoji}
    </div>
    <p className="text-base font-semibold text-gray-900 dark:text-white">{title}</p>
    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{body}</p>
    {action && <div className="mt-5 flex justify-center">{action}</div>}
  </div>
);

const SectionHeader = ({ title, subtitle, action }) => (
  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
    <div className="text-center sm:text-left">
      <h2 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">{title}</h2>
      {subtitle && <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p>}
    </div>
    {action && <div className="flex justify-center">{action}</div>}
  </div>
);

/* ─────────────────────────────── page ─────────────────────────────── */

const BrandingTemplate = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState(null);
  const [kit, setKit] = useState(null);
  const [activeTab, setActiveTab] = useState('logos');
  const [copied, setCopied] = useState('');

  const isOwner = userRole === 'Owner';

  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;
    getUserData(currentUser.uid).then((userData) => {
      if (cancelled) return;
      if (userData?.userType === 'internal' && userData.role === 'Owner') {
        setUserRole('Owner');
      } else {
        setUserRole(userData?.role || 'none');
        navigate('/crm/templates', { replace: true });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [currentUser, navigate]);

  useEffect(() => {
    if (!isOwner) return undefined;
    return onSnapshot(
      KIT_DOC,
      (snap) => setKit(snap.exists() ? snap.data() : {}),
      (err) => {
        console.error('Error loading brand kit:', err);
        setKit({});
      }
    );
  }, [isOwner]);

  const saveFields = async (fields) => {
    await setDoc(
      KIT_DOC,
      { ...fields, updatedAt: serverTimestamp(), updatedBy: currentUser?.email || currentUser?.uid || null },
      { merge: true }
    );
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied((k) => (k === key ? '' : k)), 1400);
    });
  };

  const uploadToKit = async (folder, file) => {
    const storagePath = `brand-kit/${folder}/${Date.now()}-${safeFileName(file.name)}`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file, { contentType: file.type || undefined });
    const url = await getDownloadURL(storageRef);
    return { url, storagePath, fileName: file.name, contentType: file.type || '', size: file.size };
  };

  const removeFromStorage = async (storagePath) => {
    if (!storagePath) return;
    try {
      await deleteObject(ref(storage, storagePath));
    } catch (err) {
      // Already gone is fine; the list entry is what matters.
      if (err?.code !== 'storage/object-not-found') console.error('Error deleting brand file:', err);
    }
  };

  if (!isOwner) {
    return userRole === null ? (
      <div className="min-h-screen flex items-center justify-center relative">
        <PortalBackground />
        <div className="relative"><BBALoader size={48} /></div>
      </div>
    ) : null;
  }

  const counts = {
    logos: (kit?.logos || []).length,
    colors: (kit?.colors || []).length,
    typography: (kit?.fonts || []).length,
    files: (kit?.files || []).length,
  };

  const updatedAt = kit?.updatedAt?.toDate ? kit.updatedAt.toDate() : null;

  return (
    <div className="min-h-screen p-4 md:p-6 relative">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto">
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 mb-6">
          <div className="justify-self-start">
            <BackButton to="/crm/templates" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-gray-900 dark:text-white text-center">
            <span className="mr-2">✨</span>Branding
          </h1>
          <div className="justify-self-end">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-3 py-1 text-xs font-medium text-amber-700 dark:text-amber-300">
              <Lock className="w-3.5 h-3.5" />
              Owner only
            </span>
          </div>
        </div>
        <p className="text-center text-sm text-gray-500 dark:text-gray-400 -mt-3 mb-6">
          The Builders Breakthrough brand kit — the one place for our logos, colors, fonts, and voice.
          {updatedAt && <span className="block text-xs mt-1">Last updated {updatedAt.toLocaleString()}</span>}
        </p>

        {/* Tabs */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex flex-wrap justify-center gap-1 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white/80 dark:bg-gray-900/80 backdrop-blur p-1 shadow-sm">
            {TABS.map((tab) => {
              const active = activeTab === tab.id;
              const count = counts[tab.id];
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition ${
                    active
                      ? 'bg-red-600 text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <span>{tab.emoji}</span>
                  {tab.label}
                  {count > 0 && (
                    <span
                      className={`rounded-full px-1.5 text-[11px] ${
                        active ? 'bg-white/20 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                      }`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {kit === null ? (
          <div className="flex justify-center py-16"><BBALoader size={44} /></div>
        ) : (
          <>
            {activeTab === 'logos' && (
              <LogosSection
                logos={kit.logos || []}
                onSave={(logos) => saveFields({ logos })}
                upload={(file) => uploadToKit('logos', file)}
                removeFromStorage={removeFromStorage}
                copy={copy}
                copied={copied}
              />
            )}
            {activeTab === 'colors' && (
              <ColorsSection colors={kit.colors || []} onSave={(colors) => saveFields({ colors })} copy={copy} copied={copied} />
            )}
            {activeTab === 'typography' && (
              <TypographySection fonts={kit.fonts || []} onSave={(fonts) => saveFields({ fonts })} />
            )}
            {activeTab === 'voice' && (
              <VoiceSection voice={kit.voice || {}} onSave={(voice) => saveFields({ voice })} copy={copy} copied={copied} />
            )}
            {activeTab === 'files' && (
              <FilesSection
                files={kit.files || []}
                onSave={(files) => saveFields({ files })}
                upload={(file) => uploadToKit('files', file)}
                removeFromStorage={removeFromStorage}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────── logos ─────────────────────────────── */

const LogosSection = ({ logos, onSave, upload, removeFromStorage, copy, copied }) => {
  const [editing, setEditing] = useState(null); // { id?, name, variant, background, file? }
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const openNew = () => {
    setError('');
    setEditing({ name: '', variant: 'Primary', background: 'light', file: null });
  };

  const handleSave = async () => {
    if (!editing.name.trim()) {
      setError('Give the logo a name.');
      return;
    }
    if (!editing.id && !editing.file) {
      setError('Choose an image file.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      let fileFields = {};
      if (editing.file) fileFields = await upload(editing.file);
      const existing = logos.find((l) => l.id === editing.id);
      const entry = {
        ...(existing || { id: newId(), createdAt: new Date().toISOString() }),
        name: editing.name.trim(),
        variant: editing.variant,
        background: editing.background,
        ...fileFields,
      };
      const next = existing ? logos.map((l) => (l.id === existing.id ? entry : l)) : [...logos, entry];
      await onSave(next);
      if (existing && editing.file && existing.storagePath) await removeFromStorage(existing.storagePath);
      setEditing(null);
    } catch (err) {
      console.error('Error saving logo:', err);
      setError(err.message || 'Could not save the logo.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (logo) => {
    if (!window.confirm(`Delete "${logo.name}"? The file is removed from storage too.`)) return;
    await onSave(logos.filter((l) => l.id !== logo.id));
    await removeFromStorage(logo.storagePath);
  };

  const bgFor = (background) => {
    if (background === 'dark') return { className: 'bg-gray-900', style: undefined };
    if (background === 'transparent') return { className: '', style: CHECKER_STYLE };
    return { className: 'bg-white', style: undefined };
  };

  return (
    <div>
      <SectionHeader
        title="Logos"
        subtitle="Every approved logo lockup, ready to download."
        action={
          <button type="button" onClick={openNew} className={btnPrimary}>
            <Upload className="w-4 h-4" /> Upload logo
          </button>
        }
      />

      {logos.length === 0 ? (
        <EmptyState
          emoji="🏷️"
          title="No logos yet"
          body="Upload the primary logo, icon, and any light/dark variants."
          action={
            <button type="button" onClick={openNew} className={btnPrimary}>
              <Upload className="w-4 h-4" /> Upload logo
            </button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {logos.map((logo) => {
            const bg = bgFor(logo.background);
            const ext = fileExt(logo.fileName);
            return (
              <div key={logo.id} className={`${card} rounded-2xl shadow-sm overflow-hidden group`}>
                <div className={`h-48 flex items-center justify-center p-6 border-b border-gray-100 dark:border-gray-800 ${bg.className}`} style={bg.style}>
                  {logo.url ? (
                    <img src={logo.url} alt={logo.name} className="max-h-full max-w-full object-contain" />
                  ) : (
                    <span className="text-sm text-gray-400">No file</span>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 dark:text-white truncate">{logo.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {logo.variant}
                        {ext && <span className="uppercase"> · {ext}</span>}
                        {logo.size ? ` · ${formatBytes(logo.size)}` : ''}
                      </p>
                    </div>
                    <div className="flex shrink-0">
                      <button
                        type="button"
                        className={iconBtn}
                        title="Copy link"
                        onClick={() => copy(logo.url, `logo-${logo.id}`)}
                      >
                        {copied === `logo-${logo.id}` ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                      </button>
                      <button
                        type="button"
                        className={iconBtn}
                        title="Download"
                        onClick={() => downloadRemoteFile(logo.url, logo.fileName || `${logo.name}.${ext || 'png'}`)}
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        className={iconBtn}
                        title="Edit"
                        onClick={() => {
                          setError('');
                          setEditing({ id: logo.id, name: logo.name, variant: logo.variant || 'Other', background: logo.background || 'light', file: null });
                        }}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button type="button" className={`${iconBtn} hover:text-red-600`} title="Delete" onClick={() => handleDelete(logo)}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <Modal
          title={editing.id ? 'Edit logo' : 'Upload logo'}
          onClose={() => !saving && setEditing(null)}
          footer={
            <>
              <button type="button" className={btnSecondary} onClick={() => setEditing(null)} disabled={saving}>Cancel</button>
              <button type="button" className={btnPrimary} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          }
        >
          <div>
            <label className={LABEL_CLASS}>Name</label>
            <input
              className={`${FIELD_CLASS} px-3`}
              value={editing.name}
              onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))}
              placeholder="BBA Primary – Full Color"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL_CLASS}>Variant</label>
              <select className={`${FIELD_CLASS} px-3`} value={editing.variant} onChange={(e) => setEditing((s) => ({ ...s, variant: e.target.value }))}>
                {LOGO_VARIANTS.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className={LABEL_CLASS}>Preview on</label>
              <select className={`${FIELD_CLASS} px-3`} value={editing.background} onChange={(e) => setEditing((s) => ({ ...s, background: e.target.value }))}>
                {LOGO_BACKGROUNDS.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={LABEL_CLASS}>{editing.id ? 'Replace file (optional)' : 'Image file'}</label>
            <input
              type="file"
              accept="image/*,.svg,.eps,.ai,.pdf"
              onChange={(e) => setEditing((s) => ({ ...s, file: e.target.files?.[0] || null }))}
              className="block w-full text-sm text-gray-600 dark:text-gray-300 file:mr-3 file:rounded-lg file:border-0 file:bg-red-600 file:px-3 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-red-700"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">PNG or SVG preview best. EPS/AI/PDF upload fine but won't preview.</p>
          </div>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        </Modal>
      )}
    </div>
  );
};

/* ─────────────────────────────── colors ─────────────────────────────── */

const ColorsSection = ({ colors, onSave, copy, copied }) => {
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const openNew = () => {
    setError('');
    setEditing({ name: '', hex: '#DC2626', usage: '' });
  };

  const handleSave = async () => {
    const hex = normalizeHex(editing.hex);
    if (!editing.name.trim()) return setError('Give the color a name.');
    if (!hex) return setError('Enter a valid hex code, like #DC2626.');
    setSaving(true);
    try {
      const existing = colors.find((c) => c.id === editing.id);
      const entry = { ...(existing || { id: newId() }), name: editing.name.trim(), hex, usage: editing.usage.trim() };
      await onSave(existing ? colors.map((c) => (c.id === existing.id ? entry : c)) : [...colors, entry]);
      setEditing(null);
    } catch (err) {
      console.error('Error saving color:', err);
      setError(err.message || 'Could not save the color.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (color) => {
    if (!window.confirm(`Delete "${color.name}"?`)) return;
    await onSave(colors.filter((c) => c.id !== color.id));
  };

  const move = async (index, dir) => {
    const next = [...colors];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    await onSave(next);
  };

  return (
    <div>
      <SectionHeader
        title="Colors"
        subtitle="Click any value to copy it."
        action={
          <button type="button" onClick={openNew} className={btnPrimary}>
            <Plus className="w-4 h-4" /> Add color
          </button>
        }
      />

      {colors.length === 0 ? (
        <EmptyState
          emoji="🎨"
          title="No colors yet"
          body="Add the brand palette — primary, secondary, neutrals, and accents."
          action={
            <button type="button" onClick={openNew} className={btnPrimary}>
              <Plus className="w-4 h-4" /> Add color
            </button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {colors.map((color, index) => {
            const rgb = hexToRgb(color.hex) || { r: 0, g: 0, b: 0 };
            const cmyk = rgbToCmyk(rgb);
            const light = isLightColor(color.hex);
            const values = [
              { label: 'HEX', value: color.hex },
              { label: 'RGB', value: `${rgb.r}, ${rgb.g}, ${rgb.b}` },
              { label: 'CMYK', value: `${cmyk.c}, ${cmyk.m}, ${cmyk.y}, ${cmyk.k}` },
            ];
            return (
              <div key={color.id} className={`${card} rounded-2xl shadow-sm overflow-hidden`}>
                <button
                  type="button"
                  onClick={() => copy(color.hex, `color-${color.id}`)}
                  className={`relative h-32 w-full flex items-end justify-between p-4 text-left ${light ? 'text-gray-900' : 'text-white'}`}
                  style={{ backgroundColor: color.hex }}
                  title="Copy HEX"
                >
                  <span className="text-lg font-semibold">{color.hex}</span>
                  {copied === `color-${color.id}` && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium">
                      <Check className="w-3.5 h-3.5" /> Copied
                    </span>
                  )}
                </button>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-gray-900 dark:text-white">{color.name}</p>
                    <div className="flex -mr-2 -mt-1">
                      <button type="button" className={`${iconBtn} disabled:opacity-30`} title="Move left" onClick={() => move(index, -1)} disabled={index === 0}>
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button type="button" className={`${iconBtn} disabled:opacity-30`} title="Move right" onClick={() => move(index, 1)} disabled={index === colors.length - 1}>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        className={iconBtn}
                        title="Edit"
                        onClick={() => {
                          setError('');
                          setEditing({ id: color.id, name: color.name, hex: color.hex, usage: color.usage || '' });
                        }}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button type="button" className={`${iconBtn} hover:text-red-600`} title="Delete" onClick={() => handleDelete(color)}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {color.usage && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{color.usage}</p>}
                  <div className="mt-3 space-y-1">
                    {values.map((v) => {
                      const key = `color-${color.id}-${v.label}`;
                      return (
                        <button
                          key={v.label}
                          type="button"
                          onClick={() => copy(v.value, key)}
                          className="w-full flex items-center justify-between rounded-lg px-2 py-1 text-xs hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <span className="font-medium text-gray-400">{v.label}</span>
                          <span className="font-mono text-gray-700 dark:text-gray-200 inline-flex items-center gap-1">
                            {copied === key && <Check className="w-3 h-3 text-green-600" />}
                            {v.value}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <Modal
          title={editing.id ? 'Edit color' : 'Add color'}
          onClose={() => !saving && setEditing(null)}
          footer={
            <>
              <button type="button" className={btnSecondary} onClick={() => setEditing(null)} disabled={saving}>Cancel</button>
              <button type="button" className={btnPrimary} onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </>
          }
        >
          <div>
            <label className={LABEL_CLASS}>Name</label>
            <input
              className={`${FIELD_CLASS} px-3`}
              value={editing.name}
              onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))}
              placeholder="BBA Red"
              autoFocus
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>Color</label>
            <div className="flex gap-3">
              <input
                type="color"
                value={normalizeHex(editing.hex) || '#000000'}
                onChange={(e) => setEditing((s) => ({ ...s, hex: e.target.value.toUpperCase() }))}
                className="h-12 w-16 shrink-0 cursor-pointer rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-1"
              />
              <input
                className={`${FIELD_CLASS} px-3 font-mono`}
                value={editing.hex}
                onChange={(e) => setEditing((s) => ({ ...s, hex: e.target.value }))}
                placeholder="#DC2626"
              />
            </div>
          </div>
          <div>
            <label className={LABEL_CLASS}>Usage</label>
            <input
              className={`${FIELD_CLASS} px-3`}
              value={editing.usage}
              onChange={(e) => setEditing((s) => ({ ...s, usage: e.target.value }))}
              placeholder="Primary CTAs, headlines, accents"
            />
          </div>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        </Modal>
      )}
    </div>
  );
};

/* ───────────────────────────── typography ───────────────────────────── */

const SAMPLE_TEXT = 'Builders Breakthrough helps remodelers build a better business.';

const TypographySection = ({ fonts, onSave }) => {
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Load Google Fonts so previews render in the real face.
  const googleHrefs = useMemo(
    () => fonts.filter((f) => f.googleFont && f.family).map((f) => googleFontHref(f.family, f.weights)),
    [fonts]
  );
  useEffect(() => {
    const links = googleHrefs.map((href) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.dataset.brandKitFont = 'true';
      document.head.appendChild(link);
      return link;
    });
    return () => links.forEach((l) => l.remove());
  }, [googleHrefs]);

  const openNew = () => {
    setError('');
    setEditing({ family: '', role: 'Heading', weights: '400, 700', googleFont: true, sourceUrl: '', notes: '' });
  };

  const handleSave = async () => {
    if (!editing.family.trim()) return setError('Enter the font family name.');
    setSaving(true);
    try {
      const existing = fonts.find((f) => f.id === editing.id);
      const entry = {
        ...(existing || { id: newId() }),
        family: editing.family.trim(),
        role: editing.role,
        weights: editing.weights.trim(),
        googleFont: !!editing.googleFont,
        sourceUrl: editing.sourceUrl.trim(),
        notes: editing.notes.trim(),
      };
      await onSave(existing ? fonts.map((f) => (f.id === existing.id ? entry : f)) : [...fonts, entry]);
      setEditing(null);
    } catch (err) {
      console.error('Error saving font:', err);
      setError(err.message || 'Could not save the font.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (font) => {
    if (!window.confirm(`Delete "${font.family}"?`)) return;
    await onSave(fonts.filter((f) => f.id !== font.id));
  };

  return (
    <div>
      <SectionHeader
        title="Typography"
        subtitle="Typefaces and how we use them."
        action={
          <button type="button" onClick={openNew} className={btnPrimary}>
            <Plus className="w-4 h-4" /> Add font
          </button>
        }
      />

      {fonts.length === 0 ? (
        <EmptyState
          emoji="🔤"
          title="No fonts yet"
          body="Add the heading and body typefaces. Google Fonts preview live."
          action={
            <button type="button" onClick={openNew} className={btnPrimary}>
              <Plus className="w-4 h-4" /> Add font
            </button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {fonts.map((font) => {
            const weights = String(font.weights || '')
              .split(/[\s,]+/)
              .map((w) => parseInt(w, 10))
              .filter((w) => w >= 100 && w <= 900);
            const family = `'${font.family}', system-ui, sans-serif`;
            const sourceHref = font.sourceUrl || (font.googleFont ? `https://fonts.google.com/specimen/${encodeURIComponent(font.family).replace(/%20/g, '+')}` : '');
            return (
              <div key={font.id} className={`${card} rounded-2xl shadow-sm p-6`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <span className="inline-flex rounded-full border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-red-700 dark:text-red-300">
                      {font.role}
                    </span>
                    <p className="mt-2 text-3xl text-gray-900 dark:text-white" style={{ fontFamily: family }}>
                      {font.family}
                    </p>
                  </div>
                  <div className="flex shrink-0 -mr-2">
                    {sourceHref && (
                      <a href={sourceHref} target="_blank" rel="noopener noreferrer" className={iconBtn} title="Open font source">
                        <Download className="w-4 h-4" />
                      </a>
                    )}
                    <button
                      type="button"
                      className={iconBtn}
                      title="Edit"
                      onClick={() => {
                        setError('');
                        setEditing({
                          id: font.id,
                          family: font.family,
                          role: font.role || 'Heading',
                          weights: font.weights || '',
                          googleFont: !!font.googleFont,
                          sourceUrl: font.sourceUrl || '',
                          notes: font.notes || '',
                        });
                      }}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button type="button" className={`${iconBtn} hover:text-red-600`} title="Delete" onClick={() => handleDelete(font)}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <p className="mt-4 text-5xl text-gray-900 dark:text-white" style={{ fontFamily: family }}>Aa</p>
                <div className="mt-4 space-y-2">
                  {(weights.length ? weights : [400]).map((w) => (
                    <p key={w} className="text-lg text-gray-800 dark:text-gray-200 truncate" style={{ fontFamily: family, fontWeight: w }}>
                      <span className="inline-block w-12 text-xs font-mono text-gray-400 align-middle" style={{ fontFamily: 'ui-monospace, monospace' }}>{w}</span>
                      {SAMPLE_TEXT}
                    </p>
                  ))}
                </div>
                {font.notes && <p className="mt-4 text-sm text-gray-500 dark:text-gray-400 whitespace-pre-line">{font.notes}</p>}
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <Modal
          title={editing.id ? 'Edit font' : 'Add font'}
          onClose={() => !saving && setEditing(null)}
          footer={
            <>
              <button type="button" className={btnSecondary} onClick={() => setEditing(null)} disabled={saving}>Cancel</button>
              <button type="button" className={btnPrimary} onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </>
          }
        >
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL_CLASS}>Font family</label>
              <input
                className={`${FIELD_CLASS} px-3`}
                value={editing.family}
                onChange={(e) => setEditing((s) => ({ ...s, family: e.target.value }))}
                placeholder="Montserrat"
                autoFocus
              />
            </div>
            <div>
              <label className={LABEL_CLASS}>Role</label>
              <select className={`${FIELD_CLASS} px-3`} value={editing.role} onChange={(e) => setEditing((s) => ({ ...s, role: e.target.value }))}>
                {FONT_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={LABEL_CLASS}>Weights</label>
            <input
              className={`${FIELD_CLASS} px-3`}
              value={editing.weights}
              onChange={(e) => setEditing((s) => ({ ...s, weights: e.target.value }))}
              placeholder="400, 600, 800"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={editing.googleFont}
              onChange={(e) => setEditing((s) => ({ ...s, googleFont: e.target.checked }))}
              className="rounded border-gray-300 text-red-600 focus:ring-red-500"
            />
            Google Font (loads a live preview)
          </label>
          <div>
            <label className={LABEL_CLASS}>Source / download link (optional)</label>
            <input
              className={`${FIELD_CLASS} px-3`}
              value={editing.sourceUrl}
              onChange={(e) => setEditing((s) => ({ ...s, sourceUrl: e.target.value }))}
              placeholder="https://fonts.google.com/specimen/Montserrat"
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>Usage notes</label>
            <textarea
              rows={3}
              className={`${FIELD_CLASS} px-3`}
              value={editing.notes}
              onChange={(e) => setEditing((s) => ({ ...s, notes: e.target.value }))}
              placeholder="Headlines in 800, all caps, tight tracking."
            />
          </div>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        </Modal>
      )}
    </div>
  );
};

/* ─────────────────────────── voice & messaging ─────────────────────────── */

const VOICE_TEXT_FIELDS = [
  { key: 'tagline', label: 'Tagline', rows: 1, placeholder: 'Build a better business.' },
  { key: 'mission', label: 'Mission', rows: 3, placeholder: 'Why BBA exists.' },
  { key: 'elevatorPitch', label: 'Elevator pitch', rows: 3, placeholder: 'The 30-second version of what we do.' },
  { key: 'boilerplate', label: 'Boilerplate / About BBA', rows: 5, placeholder: 'The standard paragraph for press, bios, and proposals.' },
];

const VoiceSection = ({ voice, onSave, copy, copied }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setDraft({
      tagline: voice.tagline || '',
      mission: voice.mission || '',
      elevatorPitch: voice.elevatorPitch || '',
      boilerplate: voice.boilerplate || '',
      toneWords: (voice.toneWords || []).join(', '),
      dos: (voice.dos || []).join('\n'),
      donts: (voice.donts || []).join('\n'),
    });
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        tagline: draft.tagline.trim(),
        mission: draft.mission.trim(),
        elevatorPitch: draft.elevatorPitch.trim(),
        boilerplate: draft.boilerplate.trim(),
        toneWords: draft.toneWords.split(',').map((w) => w.trim()).filter(Boolean),
        dos: linesToList(draft.dos),
        donts: linesToList(draft.donts),
      });
      setEditing(false);
    } catch (err) {
      console.error('Error saving voice:', err);
      alert('Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const hasAny =
    VOICE_TEXT_FIELDS.some((f) => voice[f.key]) || (voice.toneWords || []).length || (voice.dos || []).length || (voice.donts || []).length;

  if (editing) {
    return (
      <div>
        <SectionHeader
          title="Voice & Messaging"
          subtitle="How we sound, and the words we reuse."
          action={
            <div className="flex gap-2">
              <button type="button" className={btnSecondary} onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
              <button type="button" className={btnPrimary} onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          }
        />
        <div className={`${card} rounded-2xl shadow-sm p-6 space-y-5`}>
          {VOICE_TEXT_FIELDS.map((f) => (
            <div key={f.key}>
              <label className={LABEL_CLASS}>{f.label}</label>
              {f.rows === 1 ? (
                <input
                  className={`${FIELD_CLASS} px-3`}
                  value={draft[f.key]}
                  onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                />
              ) : (
                <textarea
                  rows={f.rows}
                  className={`${FIELD_CLASS} px-3`}
                  value={draft[f.key]}
                  onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                />
              )}
            </div>
          ))}
          <div>
            <label className={LABEL_CLASS}>Tone words (comma separated)</label>
            <input
              className={`${FIELD_CLASS} px-3`}
              value={draft.toneWords}
              onChange={(e) => setDraft((d) => ({ ...d, toneWords: e.target.value }))}
              placeholder="Direct, confident, practical, encouraging"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={LABEL_CLASS}>Do (one per line)</label>
              <textarea rows={6} className={`${FIELD_CLASS} px-3`} value={draft.dos} onChange={(e) => setDraft((d) => ({ ...d, dos: e.target.value }))} />
            </div>
            <div>
              <label className={LABEL_CLASS}>Don't (one per line)</label>
              <textarea rows={6} className={`${FIELD_CLASS} px-3`} value={draft.donts} onChange={(e) => setDraft((d) => ({ ...d, donts: e.target.value }))} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SectionHeader
        title="Voice & Messaging"
        subtitle="How we sound, and the words we reuse."
        action={
          <button type="button" className={btnPrimary} onClick={startEdit}>
            <Pencil className="w-4 h-4" /> Edit
          </button>
        }
      />
      {!hasAny ? (
        <EmptyState
          emoji="🗣️"
          title="No voice guidelines yet"
          body="Add the tagline, mission, boilerplate, and tone so everyone writes like BBA."
          action={<button type="button" className={btnPrimary} onClick={startEdit}><Pencil className="w-4 h-4" /> Add guidelines</button>}
        />
      ) : (
        <div className="space-y-5">
          {voice.tagline && (
            <div className={`${card} rounded-2xl shadow-sm p-8 text-center`}>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Tagline</p>
              <p className="text-2xl md:text-3xl font-bold tracking-tight text-gray-900 dark:text-white">{voice.tagline}</p>
            </div>
          )}
          {(voice.toneWords || []).length > 0 && (
            <div className={`${card} rounded-2xl shadow-sm p-6 text-center`}>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">Tone</p>
              <div className="flex flex-wrap justify-center gap-2">
                {voice.toneWords.map((w) => (
                  <span key={w} className="rounded-full border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-1 text-sm font-medium text-red-700 dark:text-red-300">
                    {w}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {VOICE_TEXT_FIELDS.filter((f) => f.key !== 'tagline' && voice[f.key]).map((f) => (
              <div key={f.key} className={`${card} rounded-2xl shadow-sm p-6 ${f.key === 'boilerplate' ? 'md:col-span-2' : ''}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">{f.label}</p>
                  <button type="button" className={iconBtn} title="Copy" onClick={() => copy(voice[f.key], `voice-${f.key}`)}>
                    {copied === `voice-${f.key}` ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-200 whitespace-pre-line">{voice[f.key]}</p>
              </div>
            ))}
          </div>
          {((voice.dos || []).length > 0 || (voice.donts || []).length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className={`${card} rounded-2xl shadow-sm p-6`}>
                <p className="text-xs font-semibold uppercase tracking-wider text-green-600 dark:text-green-400 mb-3">✅ Do</p>
                <ul className="space-y-2">
                  {(voice.dos || []).map((d, i) => (
                    <li key={i} className="text-sm text-gray-700 dark:text-gray-200 flex gap-2"><span className="text-green-500">•</span>{d}</li>
                  ))}
                </ul>
              </div>
              <div className={`${card} rounded-2xl shadow-sm p-6`}>
                <p className="text-xs font-semibold uppercase tracking-wider text-red-600 dark:text-red-400 mb-3">🚫 Don't</p>
                <ul className="space-y-2">
                  {(voice.donts || []).map((d, i) => (
                    <li key={i} className="text-sm text-gray-700 dark:text-gray-200 flex gap-2"><span className="text-red-500">•</span>{d}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────── files ─────────────────────────────── */

const FILE_EMOJI = { pdf: '📕', ai: '🎨', eps: '🎨', psd: '🖌️', fig: '🧩', zip: '🗜️', docx: '📄', doc: '📄', pptx: '📊', key: '📊', png: '🖼️', jpg: '🖼️', jpeg: '🖼️', svg: '🖼️', mp4: '🎬', mov: '🎬', ttf: '🔤', otf: '🔤', woff: '🔤', woff2: '🔤' };

const FilesSection = ({ files, onSave, upload, removeFromStorage }) => {
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [category, setCategory] = useState('all');

  const openNew = () => {
    setError('');
    setEditing({ name: '', category: 'Brand Guidelines', notes: '', file: null });
  };

  const handleSave = async () => {
    if (!editing.id && !editing.file) return setError('Choose a file.');
    const name = editing.name.trim() || editing.file?.name?.replace(/\.[^.]+$/, '') || '';
    if (!name) return setError('Give the file a name.');
    setSaving(true);
    setError('');
    try {
      let fileFields = {};
      if (editing.file) fileFields = await upload(editing.file);
      const existing = files.find((f) => f.id === editing.id);
      const entry = {
        ...(existing || { id: newId(), createdAt: new Date().toISOString() }),
        name,
        category: editing.category,
        notes: editing.notes.trim(),
        ...fileFields,
      };
      await onSave(existing ? files.map((f) => (f.id === existing.id ? entry : f)) : [...files, entry]);
      if (existing && editing.file && existing.storagePath) await removeFromStorage(existing.storagePath);
      setEditing(null);
    } catch (err) {
      console.error('Error saving brand file:', err);
      setError(err.message || 'Could not save the file.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (file) => {
    if (!window.confirm(`Delete "${file.name}"? The file is removed from storage too.`)) return;
    await onSave(files.filter((f) => f.id !== file.id));
    await removeFromStorage(file.storagePath);
  };

  const usedCategories = FILE_CATEGORIES.filter((c) => files.some((f) => f.category === c));
  const shown = category === 'all' ? files : files.filter((f) => f.category === category);

  return (
    <div>
      <SectionHeader
        title="Files"
        subtitle="Brand guidelines, templates, and source files."
        action={
          <button type="button" onClick={openNew} className={btnPrimary}>
            <Upload className="w-4 h-4" /> Upload file
          </button>
        }
      />

      {files.length === 0 ? (
        <EmptyState
          emoji="📁"
          title="No files yet"
          body="Upload the brand guidelines PDF, letterhead, email signature, social templates, and source files."
          action={
            <button type="button" onClick={openNew} className={btnPrimary}>
              <Upload className="w-4 h-4" /> Upload file
            </button>
          }
        />
      ) : (
        <>
          {usedCategories.length > 1 && (
            <div className="flex flex-wrap justify-center gap-2 mb-5">
              {['all', ...usedCategories].map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCategory(c)}
                  className={`rounded-full px-3 py-1 text-xs font-medium border transition ${
                    category === c
                      ? 'border-red-600 bg-red-600 text-white'
                      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  {c === 'all' ? 'All' : c}
                </button>
              ))}
            </div>
          )}
          <div className={`${card} rounded-2xl shadow-sm overflow-hidden`}>
            {shown.map((file) => {
              const ext = fileExt(file.fileName);
              return (
                <div key={file.id} className="flex items-center gap-4 px-5 py-4 border-b border-gray-100 dark:border-gray-800 last:border-b-0">
                  <div className="w-11 h-11 shrink-0 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-xl">
                    {FILE_EMOJI[ext] || '📎'}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 dark:text-white truncate">{file.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {file.category}
                      {ext && <span className="uppercase"> · {ext}</span>}
                      {file.size ? ` · ${formatBytes(file.size)}` : ''}
                      {file.notes ? ` · ${file.notes}` : ''}
                    </p>
                  </div>
                  <div className="flex shrink-0">
                    <button
                      type="button"
                      className={iconBtn}
                      title="Download"
                      onClick={() => downloadRemoteFile(file.url, file.fileName || file.name)}
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      className={iconBtn}
                      title="Edit"
                      onClick={() => {
                        setError('');
                        setEditing({ id: file.id, name: file.name, category: file.category || 'Other', notes: file.notes || '', file: null });
                      }}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button type="button" className={`${iconBtn} hover:text-red-600`} title="Delete" onClick={() => handleDelete(file)}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {editing && (
        <Modal
          title={editing.id ? 'Edit file' : 'Upload file'}
          onClose={() => !saving && setEditing(null)}
          footer={
            <>
              <button type="button" className={btnSecondary} onClick={() => setEditing(null)} disabled={saving}>Cancel</button>
              <button type="button" className={btnPrimary} onClick={handleSave} disabled={saving}>{saving ? 'Uploading…' : 'Save'}</button>
            </>
          }
        >
          <div>
            <label className={LABEL_CLASS}>{editing.id ? 'Replace file (optional)' : 'File'}</label>
            <input
              type="file"
              onChange={(e) => setEditing((s) => ({ ...s, file: e.target.files?.[0] || null }))}
              className="block w-full text-sm text-gray-600 dark:text-gray-300 file:mr-3 file:rounded-lg file:border-0 file:bg-red-600 file:px-3 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-red-700"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL_CLASS}>Name</label>
              <input
                className={`${FIELD_CLASS} px-3`}
                value={editing.name}
                onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))}
                placeholder={editing.file?.name || 'Brand Guidelines 2026'}
              />
            </div>
            <div>
              <label className={LABEL_CLASS}>Category</label>
              <select className={`${FIELD_CLASS} px-3`} value={editing.category} onChange={(e) => setEditing((s) => ({ ...s, category: e.target.value }))}>
                {FILE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={LABEL_CLASS}>Notes (optional)</label>
            <input
              className={`${FIELD_CLASS} px-3`}
              value={editing.notes}
              onChange={(e) => setEditing((s) => ({ ...s, notes: e.target.value }))}
              placeholder="Use for all client-facing proposals"
            />
          </div>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        </Modal>
      )}
    </div>
  );
};

export default BrandingTemplate;
